import { useApolloClient } from '@apollo/client';
import { Alert, AlertModal, Button, Stack, Text, useNotifications } from '@sixfold/common-ui';
import React, { useState } from 'react';
import { Link } from 'react-router';

import { RelationshipCompanyIdentifier } from '../../company/entities';
import { ConfirmButton } from '../../components/confirm_button';
import { CompanyRelationshipType } from '../../lib/graphql';
import { Routes } from '../../routes';
import { DeleteCompanyRelationshipOptions } from '../containers/relationship_mutations_container';
import { fetchGroupAccountsSharingCarrier, GroupAccount } from '../fetch_group_accounts';

interface Props {
  identifiers: RelationshipCompanyIdentifier[];
  disabled: boolean;
  carrier: { companyId: string; companyName: string | null };
  targetCompanyId: string;
  relationshipType: CompanyRelationshipType;
  onDelete: (options?: DeleteCompanyRelationshipOptions) => Promise<unknown>;
}

export const DeleteRelationshipAction: React.FC<Props> = ({
  identifiers,
  disabled,
  carrier,
  targetCompanyId,
  relationshipType,
  onDelete,
}) => {
  const apolloClient = useApolloClient();
  const notify = useNotifications();
  const [isCheckingGroup, setIsCheckingGroup] = useState(false);
  const [isInlineConfirmOpen, setIsInlineConfirmOpen] = useState(false);
  const [isIdentifierWarningOpen, setIsIdentifierWarningOpen] = useState(false);
  const [groupAccounts, setGroupAccounts] = useState<GroupAccount[]>([]);
  const [isGroupWarningOpen, setIsGroupWarningOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const hasIdentifiers = identifiers.length > 0;
  const isPlural = identifiers.length > 1;
  const carrierName = carrier.companyName ?? `#${carrier.companyId}`;
  const hub = groupAccounts.find(({ isHub }) => isHub);

  const startDelete = async () => {
    setIsCheckingGroup(true);
    try {
      const accounts = await fetchGroupAccountsSharingCarrier(apolloClient, {
        carrierId: carrier.companyId,
        targetCompanyId,
        relationshipType,
      });
      if (accounts.length > 1) {
        setGroupAccounts(accounts);
        setIsGroupWarningOpen(true);
      } else if (hasIdentifiers) {
        setIsIdentifierWarningOpen(true);
      } else {
        setIsInlineConfirmOpen(true);
      }
    } catch {
      notify.error({ title: 'Could not check whether this relationship is shared in a company group' });
    } finally {
      setIsCheckingGroup(false);
    }
  };

  const handleConfirm = async (close: () => void, options?: DeleteCompanyRelationshipOptions) => {
    setIsDeleting(true);
    try {
      await onDelete(options);
      close();
    } finally {
      setIsDeleting(false);
    }
  };

  const identifierList = identifiers.map((i, idx) => (
    <React.Fragment key={i.companyIdentifierId}>
      {idx > 0 && ', '}
      <Text weight="bold">{i.identifierValue}</Text>
    </React.Fragment>
  ));

  const identifierWarning = (
    <>
      <Text>
        This relationship has the following identifier{isPlural ? 's' : ''} attached: {identifierList}.
      </Text>
      <Text>
        Deleting the relationship will also remove {isPlural ? 'these identifiers' : 'this identifier'} and may break
        integrations or data sharing that depend on {isPlural ? 'them' : 'it'}.
      </Text>
    </>
  );

  const groupModal = (
    <AlertModal
      open={isGroupWarningOpen}
      onClose={() => !isDeleting && setIsGroupWarningOpen(false)}
      trigger={null}
      title={`Remove ${carrierName} from all ${groupAccounts.length} accounts in this group?`}
      description={
        <Stack direction="column" gap="small">
          <Alert kind="danger">
            <Text>
              This {relationshipType === CompanyRelationshipType.SUBCARRIER ? 'subcarrier' : 'carrier'} is shared across
              the company group of{' '}
              {hub !== undefined && (
                <Link
                  to={Routes.CompanyGroup.generatePath({ company_id: hub.companyId })}
                  target="_blank"
                  rel="noopener noreferrer">
                  {hub.companyName}
                </Link>
              )}
              . Deleting this relationship removes it from all {groupAccounts.length} accounts:
            </Text>
            <ul style={{ margin: 0, maxHeight: 240, overflowY: 'auto' }}>
              {groupAccounts.map(({ companyId, companyName, isHub }) => (
                <li key={companyId}>
                  {companyName}
                  {isHub && ' (hub)'}
                </li>
              ))}
            </ul>
          </Alert>
          {hasIdentifiers && identifierWarning}
        </Stack>
      }>
      <Button kind="subtle" onClick={() => setIsGroupWarningOpen(false)} disabled={isDeleting}>
        Cancel
      </Button>
      <Button
        kind="danger"
        onClick={() =>
          handleConfirm(() => setIsGroupWarningOpen(false), {
            successTitle: `${carrierName} removed from ${groupAccounts.length} accounts`,
          })
        }
        disabled={isDeleting}
        loading={isDeleting}>
        Remove from {groupAccounts.length} accounts
      </Button>
    </AlertModal>
  );

  if (!hasIdentifiers) {
    return (
      <>
        <ConfirmButton
          label="Delete"
          disabled={disabled || isCheckingGroup}
          initialButtonColor="red"
          size="tiny"
          showConfirmationProp={isInlineConfirmOpen}
          updateConfirmState={(next) => (next ? void startDelete() : setIsInlineConfirmOpen(false))}
          onConfirm={async () => {
            await onDelete();
          }}
        />
        {groupModal}
      </>
    );
  }

  return (
    <>
      <Button
        kind="danger"
        size="small"
        disabled={disabled}
        loading={isCheckingGroup}
        onClick={() => void startDelete()}>
        Delete
      </Button>
      <AlertModal
        open={isIdentifierWarningOpen}
        onClose={() => !isDeleting && setIsIdentifierWarningOpen(false)}
        trigger={null}
        title="Delete relationship with identifier?"
        description={
          <Stack direction="column" gap="small">
            {identifierWarning}
          </Stack>
        }>
        <Button kind="subtle" onClick={() => setIsIdentifierWarningOpen(false)} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          kind="danger"
          onClick={() => handleConfirm(() => setIsIdentifierWarningOpen(false))}
          disabled={isDeleting}
          loading={isDeleting}>
          Delete relationship
        </Button>
      </AlertModal>
      {groupModal}
    </>
  );
};
